import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ViewChild, Input, Output, NgModule } from '@angular/core';
import { BaseFirestoreRepository, FirestoreQueryBuilder } from '@engineering11/web-api-firebase';
import * as _ from 'lodash';
import * as i1 from '@engineering11/files-web';
import { deepCopy, htmlId } from '@engineering11/utility';
import { map as map$1, combineLatest, of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@engineering11/ui-lib/e11-autocomplete';
import { E11AutocompleteModule } from '@engineering11/ui-lib/e11-autocomplete';
import * as i4 from '@engineering11/ui-lib/e11-tooltip';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';
import * as i6 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i1$1 from '@engineering11/ui-lib/e11-popover';
import * as i5 from '@engineering11/ui-lib/e11-avatar';
import { E11AvatarModule } from '@engineering11/ui-lib/e11-avatar';
import * as i6$1 from '@engineering11/web-utilities';
import { WebUtilityModule } from '@engineering11/web-utilities';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import * as i2$1 from '@engineering11/web-api-error';
import * as i3$1 from '@ngrx/component';
import { LetDirective } from '@ngrx/component';
import * as i4$1 from '@engineering11/ui-lib/e11-profile-pic';
import { E11ProfilePicModule } from '@engineering11/ui-lib/e11-profile-pic';

const DEFAULT_LANGUAGE_CODE = 'en-US';
const SDK_NAME = 'content';
var FILES;
(function (FILES) {
    FILES["MIME_TYPE_VIDEO_PREFIX"] = "video/";
    FILES["CONTENT_FILE_SIGNATURE_VALUE"] = "40c20c6f-ddcd-4e00-9323-c55b30cc2e06";
    FILES["CONTENT_FILE_SIGNATURE_NAME"] = "signature";
})(FILES || (FILES = {}));
var CONTENT_STATE;
(function (CONTENT_STATE) {
    CONTENT_STATE["DELETING"] = "deleting";
    CONTENT_STATE["ARCHIVED"] = "archived";
    CONTENT_STATE["ACTIVE"] = "active";
    CONTENT_STATE["LOCKED"] = "locked";
})(CONTENT_STATE || (CONTENT_STATE = {}));
var ContentPublishState;
(function (ContentPublishState) {
    ContentPublishState["Published"] = "Published";
    ContentPublishState["Publishing"] = "Publishing";
})(ContentPublishState || (ContentPublishState = {}));
const ERROR_TYPES = Object.freeze({
    CONTENT_INVALID_REQUEST: `${SDK_NAME}/general/invalid_request`,
});
var CONTENT_ASSOCIATION;
(function (CONTENT_ASSOCIATION) {
    CONTENT_ASSOCIATION["REVIEWER"] = "reviewer";
    CONTENT_ASSOCIATION["EDITOR"] = "editor";
    CONTENT_ASSOCIATION["APPROVER"] = "approver";
    CONTENT_ASSOCIATION["OWNER"] = "owner";
})(CONTENT_ASSOCIATION || (CONTENT_ASSOCIATION = {}));
var COLLECTIONS;
(function (COLLECTIONS) {
    COLLECTIONS["COLLECTION_CONTENT"] = "content/";
    COLLECTIONS["COLLECTION_CONTENT_REVISION"] = "/revision_history/";
    COLLECTIONS["COLLECTION_CONTENT_REVIEWS"] = "/reviews/";
    COLLECTIONS["COLLECTION_CONTENT_REVIEWS_REQUESTS"] = "/requests/";
    COLLECTIONS["COLLECTION_CONTENT_APPROVALS"] = "/approvals/";
    COLLECTIONS["COLLECTION_CONTENT_APPROVALS_REQUESTS"] = "/requests/";
    COLLECTIONS["COLLECTION_CONTENT_ITEM_SNAPSHOT"] = "/snapshot/";
    COLLECTIONS["COLLECTION_CONTENT_ITEM_COMMENTS"] = "/comments/";
    COLLECTIONS["COLLECTION_CONTENT_DOCUMENT"] = "/document/";
    COLLECTIONS["COLLECTION_CONTENT_FILES"] = "/files/";
    COLLECTIONS["COLLECTION_CONTENT_PUBLISHED"] = "content_published/";
    COLLECTIONS["COLLECTION_CONTENT_PUBLISH_LOG"] = "/publish_log/";
    COLLECTIONS["COLLECTION_CONTENT_VERSIONS"] = "/versions/";
})(COLLECTIONS || (COLLECTIONS = {}));

class ContentApprovalRepository extends BaseFirestoreRepository {
    buildPath(contentId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_APPROVALS}${id}`;
    }
    get(contentId, approvalId) {
        const path = this.buildPath(contentId, approvalId);
        return super._get(path);
    }
    getValueChanges(contentId, approvalId) {
        const path = this.buildPath(contentId, approvalId);
        return super._getValueChanges(path);
    }
    getAll(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        model.languageCode ?? DEFAULT_LANGUAGE_CODE;
        const path = this.buildPath(model.contentId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.id);
        return super._update(model, path);
    }
    delete(contentId, approvalId) {
        const path = this.buildPath(contentId, approvalId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentApprovalRequestRepository extends BaseFirestoreRepository {
    buildPath(contentId, approvalId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_APPROVALS}${approvalId}${COLLECTIONS.COLLECTION_CONTENT_APPROVALS_REQUESTS}${id}`;
    }
    get(contentId, approvalId, requestId) {
        const path = this.buildPath(contentId, approvalId, requestId);
        return super._get(path);
    }
    getValueChanges(contentId, approvalId, requestId) {
        const path = this.buildPath(contentId, approvalId, requestId);
        return super._getValueChanges(path);
    }
    getAll(contentId, approvalId, customerKey) {
        const path = this.buildPath(contentId, approvalId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, approvalId, customerKey) {
        const path = this.buildPath(contentId, approvalId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        model.completed ?? false;
        const path = this.buildPath(model.contentId, model.approvalId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.approvalId, model.id);
        return super._update(model, path);
    }
    delete(contentId, approvalId, requestId) {
        const path = this.buildPath(contentId, approvalId, requestId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRequestRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRequestRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRequestRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentApprovalCommentRepository extends BaseFirestoreRepository {
    buildPath(contentId, approvalId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_APPROVALS}${approvalId}${COLLECTIONS.COLLECTION_CONTENT_ITEM_COMMENTS}${id}`;
    }
    get(contentId, approvalId, id) {
        const path = this.buildPath(contentId, approvalId, id);
        return super._get(path);
    }
    getValueChanges(contentId, approvalId, id) {
        const path = this.buildPath(contentId, approvalId, id);
        return super._getValueChanges(path);
    }
    getAll(contentId, approvalId, customerKey) {
        const path = this.buildPath(contentId, approvalId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, approvalId, customerKey) {
        const path = this.buildPath(contentId, approvalId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        const path = this.buildPath(model.contentId, model.approvalId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.approvalId, model.id);
        return super._update(model, path);
    }
    delete(contentId, approvalId, requestId) {
        const path = this.buildPath(contentId, approvalId, requestId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalCommentRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalCommentRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalCommentRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentApprovalRequestService {
    constructor(repository, comments) {
        this.repository = repository;
        this.comments = comments;
    }
    get(contentId, approvalId, requestId) {
        return this.repository.get(contentId, approvalId, requestId);
    }
    getValueChanges(contentId, approvalId, requestId) {
        return this.repository.getValueChanges(contentId, approvalId, requestId);
    }
    getAll(contentId, approvalId, customerKey) {
        return this.repository.getAll(contentId, approvalId, customerKey);
    }
    getAllValueChanges(contentId, approvalId, customerKey) {
        return this.repository.getAllValueChanges(contentId, approvalId, customerKey);
    }
    async add(model) {
        return this.repository.add(model);
    }
    update(model) {
        return this.repository.update(model);
    }
    delete(contentId, approvalId, requestId) {
        return this.repository.delete(contentId, approvalId, requestId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRequestService, deps: [{ token: ContentApprovalRequestRepository }, { token: ContentApprovalCommentRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRequestService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalRequestService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentApprovalRequestRepository }, { type: ContentApprovalCommentRepository }] });

class ContentApprovalService {
    constructor(repository, requests) {
        this.repository = repository;
        this.requests = requests;
    }
    get(contentId, approvalId) {
        return this.repository.get(contentId, approvalId);
    }
    getValueChanges(contentId, approvalId) {
        return this.repository.getValueChanges(contentId, approvalId);
    }
    getAll(contentId, customerKey) {
        return this.repository.getAll(contentId, customerKey);
    }
    getAllValueChanges(contentId, customerKey) {
        return this.repository.getAllValueChanges(contentId, customerKey);
    }
    async add(model) {
        return this.repository.add(model);
    }
    update(model) {
        return this.repository.update(model);
    }
    delete(contentId, approvalId) {
        return this.repository.delete(contentId, approvalId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalService, deps: [{ token: ContentApprovalRepository }, { token: ContentApprovalRequestService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentApprovalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentApprovalRepository }, { type: ContentApprovalRequestService }] });

class ContentDocumentRepository extends BaseFirestoreRepository {
    buildPath(contentId, languageCode = DEFAULT_LANGUAGE_CODE) {
        const id = this.buildId(contentId, languageCode);
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_DOCUMENT}${id}`;
    }
    buildPathNoId(contentId) {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_DOCUMENT}`;
    }
    buildId(contentId, languageCode = DEFAULT_LANGUAGE_CODE) {
        return `${contentId}_${languageCode}`;
    }
    get(contentId, languageCode) {
        const path = this.buildPath(contentId, languageCode);
        return super._get(path);
    }
    getValueChanges(contentId, languageCode) {
        const path = this.buildPath(contentId, languageCode);
        return super._getValueChanges(path);
    }
    getAll() {
        throw new Error('Unsupported Operation');
    }
    getAllValueChanges() {
        throw new Error('Unsupported Operation');
    }
    add(model) {
        if (!model.contentId || !model.contentType) {
            throw new Error('Content ID or content TYPE not set on model');
        }
        model.languageCode = model.languageCode ?? DEFAULT_LANGUAGE_CODE;
        model.id = this.buildId(model.contentId, model.languageCode);
        const path = this.buildPathNoId(model.contentId);
        return super._add(model, path);
    }
    set(model) {
        if (!model.contentId || !model.contentType) {
            throw new Error('Content ID or content TYPE not set on model');
        }
        model.languageCode = model.languageCode ?? DEFAULT_LANGUAGE_CODE;
        model.id = this.buildId(model.contentId, model.languageCode);
        const path = this.buildPath(model.contentId, model.languageCode);
        return super._set(model, path);
    }
    update(model) {
        if (!model.contentId) {
            throw new Error('Content ID not set on model');
        }
        const path = this.buildPath(model.contentId, model.languageCode);
        return super._update(model, path);
    }
    delete(contentId, languageCode) {
        const path = this.buildPath(contentId, languageCode);
        return super._delete(path);
    }
    serialize(model) {
        const modelToSave = _.cloneDeep(model);
        this.stripSignedURLs(modelToSave);
        return modelToSave;
    }
    stripSignedURLs(json) {
        const contentFiles = new Array();
        if (json instanceof Object) {
            const children = this.getChildren(json);
            if (children.length > 0) {
                children.forEach((child) => {
                    const r = this.stripSignedURLs(child);
                    contentFiles.push(...r);
                });
            }
            else if (!_.isArray(json)) {
                if (this.isAContentFile(json)) {
                    json.signedURL = undefined;
                    contentFiles.push(json);
                }
            }
        }
        return contentFiles;
    }
    isAContentFile(obj) {
        if (!obj || !(obj instanceof Object)) {
            return false;
        }
        if (obj[FILES.CONTENT_FILE_SIGNATURE_NAME] === FILES.CONTENT_FILE_SIGNATURE_VALUE) {
            return true;
        }
        return false;
    }
    getChildren(obj) {
        const children = [];
        if (!obj) {
            return children;
        }
        Object.keys(obj).forEach((k) => {
            if (obj[k] instanceof Object) {
                children.push(obj[k]);
            }
        });
        return children;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentDocumentRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentDocumentRepository }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentDocumentRepository, decorators: [{
            type: Injectable
        }] });

class ContentFileService {
    constructor(filesService) {
        this.filesService = filesService;
    }
    async delete(contentFile) {
        if (!contentFile.secretToken) {
            throw new Error('secretToken is required to delete');
        }
        return this.filesService.delete(contentFile.secretToken);
    }
    async upload(contentFile) {
        if (!contentFile.contentId) {
            throw new Error('contentId is required to upload');
        }
        if (!contentFile.customerKey) {
            throw new Error('customerKey is required to upload');
        }
        if (!contentFile.file) {
            throw new Error('No file to upload');
        }
        if (!contentFile.fileName || !contentFile.mimeType) {
            throw new Error('File name and mimeType must be specified');
        }
        const fileReceipt = await this.filesService.upload(contentFile.file, contentFile.customerKey, contentFile.contentId);
        const fullReceipt = fileReceipt;
        fullReceipt.contentId = contentFile.contentId;
        return fullReceipt;
    }
    async signedURLs(contentFiles) {
        return this.filesService.signedURLs(contentFiles);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentFileService, deps: [{ token: i1.FilesService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentFileService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentFileService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.FilesService }] });

class ContentItemRepository extends ContentDocumentRepository {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentItemRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentItemRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentItemRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentSnapshotRepository extends BaseFirestoreRepository {
    buildPath(contentId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_ITEM_SNAPSHOT}${id}`;
    }
    get(contentId, snapshotId) {
        const path = this.buildPath(contentId, snapshotId);
        return super._get(path);
    }
    getValueChanges(contentId, snapshotId) {
        const path = this.buildPath(contentId, snapshotId);
        return super._getValueChanges(path);
    }
    getAll(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        const path = this.buildPath(model.contentId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model, snapshotId) {
        const path = this.buildPath(model.contentId, snapshotId);
        return super._update(model, path);
    }
    delete(contentId, snapshotId) {
        throw new Error('Unsupported Operation');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSnapshotRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSnapshotRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSnapshotRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentReviewRepository extends BaseFirestoreRepository {
    buildPath(contentId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_REVIEWS}${id}`;
    }
    get(contentId, reviewId) {
        const path = this.buildPath(contentId, reviewId);
        return super._get(path);
    }
    getValueChanges(contentId, reviewId) {
        const path = this.buildPath(contentId, reviewId);
        return super._getValueChanges(path);
    }
    getAll(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        model.languageCode ?? DEFAULT_LANGUAGE_CODE;
        const path = this.buildPath(model.contentId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.id);
        return super._update(model, path);
    }
    delete(contentId, reviewId) {
        const path = this.buildPath(contentId, reviewId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentReviewRequestRepository extends BaseFirestoreRepository {
    buildPath(contentId, reviewId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_REVIEWS}${reviewId}${COLLECTIONS.COLLECTION_CONTENT_REVIEWS_REQUESTS}${id}`;
    }
    get(contentId, reviewId, requestId) {
        const path = this.buildPath(contentId, reviewId, requestId);
        return super._get(path);
    }
    getValueChanges(contentId, reviewId, requestId) {
        const path = this.buildPath(contentId, reviewId, requestId);
        return super._getValueChanges(path);
    }
    getAll(contentId, reviewId, customerKey) {
        const path = this.buildPath(contentId, reviewId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, reviewId, customerKey) {
        const path = this.buildPath(contentId, reviewId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        model.completed ?? false;
        const path = this.buildPath(model.contentId, model.reviewId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.reviewId, model.id);
        return super._update(model, path);
    }
    delete(contentId, reviewId, requestId) {
        const path = this.buildPath(contentId, reviewId, requestId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRequestRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRequestRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRequestRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentReviewCommentRepository extends BaseFirestoreRepository {
    buildPath(contentId, reviewId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_REVIEWS}${reviewId}${COLLECTIONS.COLLECTION_CONTENT_ITEM_COMMENTS}${id}`;
    }
    get(contentId, reviewId, id) {
        const path = this.buildPath(contentId, reviewId, id);
        return super._get(path);
    }
    getValueChanges(contentId, reviewId, id) {
        const path = this.buildPath(contentId, reviewId, id);
        return super._getValueChanges(path);
    }
    getAll(contentId, reviewId, customerKey) {
        const path = this.buildPath(contentId, reviewId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, reviewId, customerKey) {
        const path = this.buildPath(contentId, reviewId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        const path = this.buildPath(model.contentId, model.reviewId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.reviewId, model.id);
        return super._update(model, path);
    }
    delete(contentId, reviewId, requestId) {
        const path = this.buildPath(contentId, reviewId, requestId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewCommentRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewCommentRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewCommentRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentReviewRequestService {
    constructor(repository, comments) {
        this.repository = repository;
        this.comments = comments;
    }
    get(contentId, reviewId, requestId) {
        return this.repository.get(contentId, reviewId, requestId);
    }
    getValueChanges(contentId, reviewId, requestId) {
        return this.repository.getValueChanges(contentId, reviewId, requestId);
    }
    getAll(contentId, reviewId, customerKey) {
        return this.repository.getAll(contentId, reviewId, customerKey);
    }
    getAllValueChanges(contentId, reviewId, customerKey) {
        return this.repository.getAllValueChanges(contentId, reviewId, customerKey);
    }
    async add(model) {
        return this.repository.add(model);
    }
    update(model) {
        return this.repository.update(model);
    }
    delete(contentId, reviewId, requestId) {
        return this.repository.delete(contentId, reviewId, requestId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRequestService, deps: [{ token: ContentReviewRequestRepository }, { token: ContentReviewCommentRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRequestService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewRequestService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentReviewRequestRepository }, { type: ContentReviewCommentRepository }] });

class ContentReviewService {
    constructor(repository, requests) {
        this.repository = repository;
        this.requests = requests;
    }
    get(contentId, reviewId) {
        return this.repository.get(contentId, reviewId);
    }
    getValueChanges(contentId, reviewId) {
        return this.repository.getValueChanges(contentId, reviewId);
    }
    getAll(contentId, customerKey) {
        return this.repository.getAll(contentId, customerKey);
    }
    getAllValueChanges(contentId, customerKey) {
        return this.repository.getAllValueChanges(contentId, customerKey);
    }
    async add(model) {
        return this.repository.add(model);
    }
    update(model) {
        return this.repository.update(model);
    }
    delete(contentId, reviewId) {
        return this.repository.delete(contentId, reviewId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewService, deps: [{ token: ContentReviewRepository }, { token: ContentReviewRequestService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentReviewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentReviewRepository }, { type: ContentReviewRequestService }] });

class ContentRevisionRepository extends BaseFirestoreRepository {
    buildPath(contentId, revisionId = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_REVISION}${revisionId}`;
    }
    get(contentId, revisionId) {
        const path = this.buildPath(contentId, revisionId);
        return super._get(path);
    }
    getValueChanges(contentId, revisionId) {
        const path = this.buildPath(contentId, revisionId);
        return super._getValueChanges(path);
    }
    getAll(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    async add(model) {
        if (!model.contentId) {
            throw new Error('Content ID not present on model.`');
        }
        const path = this.buildPath(model.contentId);
        const output = await super._add(model, path);
        return output.id;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        if (!model.contentId) {
            throw new Error('Content ID not present on model.`');
        }
        const path = this.buildPath(model.contentId, model.id);
        return super._update(model, path);
    }
    delete(contentId, revisionId) {
        const path = this.buildPath(contentId, revisionId);
        return super._delete(path);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentRevisionRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentRevisionRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentRevisionRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentRevisionService {
    constructor(repository) {
        this.repository = repository;
    }
    get(contentId, revisionId) {
        return this.repository.get(contentId, revisionId);
    }
    getValueChanges(contentId, revisionId) {
        return this.repository.getValueChanges(contentId, revisionId);
    }
    getAll(contentId, customerKey) {
        return this.repository.getAll(contentId, customerKey);
    }
    getAllValueChanges(contentId, customerKey) {
        return this.repository.getAllValueChanges(contentId, customerKey);
    }
    async add(model) {
        model.timestamp = new Date();
        return this.repository.add(model);
    }
    update(model) {
        model.timestamp = new Date();
        return this.repository.update(model);
    }
    delete(contentId, revisionId) {
        return this.repository.delete(contentId, revisionId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentRevisionService, deps: [{ token: ContentRevisionRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentRevisionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentRevisionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentRevisionRepository }] });

class ContentSummaryRepository extends BaseFirestoreRepository {
    buildPath(contentId = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}`;
    }
    get(contentId) {
        const path = this.buildPath(contentId);
        return super._get(path).pipe(map((summary) => {
            if (summary) {
                return this.map(summary);
            }
            else {
                return undefined;
            }
        }));
    }
    getValueChanges(contentId) {
        const path = this.buildPath(contentId);
        return super._getValueChanges(path).pipe(map((s) => {
            if (s) {
                return this.map(s);
            }
            else {
                return undefined;
            }
        }));
    }
    getAll(customerKey, contentType) {
        const builder = new FirestoreQueryBuilder().where('customerKey', '==', customerKey).where('state', '!=', CONTENT_STATE.ARCHIVED);
        if (contentType) {
            builder.where('contentType', '==', contentType);
        }
        const customerQueryRef = builder.build();
        const path = this.buildPath();
        return super._getAll(path, customerQueryRef).pipe(map((list) => this.mapSummaryList(list)));
    }
    getAllArchived(customerKey, contentType) {
        const builder = new FirestoreQueryBuilder().where('customerKey', '==', customerKey).where('state', '==', CONTENT_STATE.ARCHIVED);
        if (contentType) {
            builder.where('contentType', '==', contentType);
        }
        const customerQueryRef = builder.build();
        const path = this.buildPath();
        return super._getAllValueChanges(path, customerQueryRef).pipe(map((list) => this.mapSummaryList(list)));
    }
    getAllValueChanges(customerKey, contentType) {
        const builder = new FirestoreQueryBuilder().where('customerKey', '==', customerKey).where('state', '!=', CONTENT_STATE.ARCHIVED);
        if (contentType) {
            builder.where('contentType', '==', contentType);
        }
        const customerQueryRef = builder.build();
        const path = this.buildPath();
        return super._getAllValueChanges(path, customerQueryRef).pipe(map((list) => this.mapSummaryList(list)));
    }
    getAllByAssociation(userId, customerKey, contentType) {
        const path = this.buildPath();
        const builder = new FirestoreQueryBuilder()
            .where('customerKey', '==', customerKey)
            .where(`associations.${userId}.id`, '==', userId)
            .where('state', '!=', CONTENT_STATE.ARCHIVED)
            .where(`associations.${userId}.associationTypes`, 'array-contains-any', [
            CONTENT_ASSOCIATION.APPROVER,
            CONTENT_ASSOCIATION.REVIEWER,
            CONTENT_ASSOCIATION.EDITOR,
            CONTENT_ASSOCIATION.OWNER,
        ]);
        if (contentType) {
            builder.where('contentType', '==', contentType);
        }
        const associationQueryRef = builder.build();
        return super._query(path, associationQueryRef).pipe(map((list) => this.mapSummaryList(list)));
    }
    getAllArchivedByAssociation(userId, customerKey, contentType) {
        const path = this.buildPath();
        const builder = new FirestoreQueryBuilder()
            .where('customerKey', '==', customerKey)
            .where(`associations.${userId}.id`, '==', userId)
            .where('state', '==', CONTENT_STATE.ARCHIVED)
            .where(`associations.${userId}.associationTypes`, 'array-contains-any', [
            CONTENT_ASSOCIATION.APPROVER,
            CONTENT_ASSOCIATION.REVIEWER,
            CONTENT_ASSOCIATION.EDITOR,
            CONTENT_ASSOCIATION.OWNER,
        ]);
        if (contentType) {
            builder.where('contentType', '==', contentType);
        }
        const associationQueryRef = builder.build();
        return super._query(path, associationQueryRef).pipe(map((list) => this.mapSummaryList(list)));
    }
    add(model) {
        if (!model.id) {
            model.id = super.createId();
        }
        model.associations = this.associationsToJSON(model.associations);
        const path = this.buildPath();
        return super._add(model, path);
    }
    set(model) {
        model.associations = this.associationsToJSON(model.associations);
        const path = this.buildPath(model.id);
        return super._set(model, path);
    }
    update(model) {
        model.associations = this.associationsToJSON(model.associations);
        const path = this.buildPath(model.id);
        return super._update(model, path);
    }
    async delete() {
        throw new Error('Operation not supported');
    }
    mapSummaryList(summaries) {
        if (!summaries || summaries.length < 1) {
            return summaries;
        }
        const mappedSummaries = new Array();
        summaries.forEach((s) => mappedSummaries.push(this.map(s)));
        return mappedSummaries;
    }
    map(summary) {
        if (!summary) {
            return summary;
        }
        const map = new Map();
        if (!summary.associations) {
            summary.associations = map;
            return summary;
        }
        const associations = summary.associations;
        Object.keys(associations).forEach((key) => {
            map.set(key, {
                ...associations[key],
                userId: associations[key].userId,
                firstName: associations[key].firstName,
                lastName: associations[key].lastName,
                associationTypes: associations[key].associationTypes,
            });
        });
        summary.associations = map;
        return summary;
    }
    associationsToJSON(associations) {
        const json = {};
        if (associations) {
            associations.forEach((value, key) => {
                json[key] = {
                    ...value,
                    userId: value.userId,
                    firstName: value.firstName,
                    lastName: value.lastName,
                    associationTypes: value.associationTypes,
                };
            });
        }
        return json;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSummaryRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSummaryRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSummaryRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const ADMINISTRATOR = 'Administrator';
class ContentSummaryService {
    constructor(repository) {
        this.repository = repository;
    }
    get(contentId) {
        return this.repository.get(contentId);
    }
    getValueChanges(contentId) {
        return this.repository.getValueChanges(contentId);
    }
    getAllForUser(user, contentType) {
        if (this.isAdminUser(user)) {
            return this.repository.getAll(user.customerKey, contentType);
        }
        else {
            return this.repository.getAllByAssociation(user.id, user.customerKey, contentType);
        }
    }
    getAll(customerKey, contentType) {
        return this.repository.getAll(customerKey, contentType);
    }
    getAllValueChanges(customerKey, contentType) {
        return this.repository.getAllValueChanges(customerKey, contentType);
    }
    getAllArchivedForUser(user, contentType) {
        if (this.isAdminUser(user)) {
            return this.repository.getAllArchived(user.customerKey, contentType);
        }
        else {
            return this.repository.getAllArchivedByAssociation(user.id, user.customerKey, contentType);
        }
    }
    add(summaryModel) {
        return this.repository.add(summaryModel);
    }
    delete(summaryModel) {
        summaryModel.state = CONTENT_STATE.DELETING;
        return this.repository.update(summaryModel);
    }
    updateAssociations(contentId, associations) {
        return this.repository.update({ id: contentId, associations: associations });
    }
    lock(summaryModel) {
        summaryModel.state = CONTENT_STATE.DELETING;
        return this.repository.update(summaryModel);
    }
    unLock(summaryModel) {
        summaryModel.state = CONTENT_STATE.ACTIVE;
        return this.repository.update(summaryModel);
    }
    archive(summaryModel) {
        summaryModel.state = CONTENT_STATE.ARCHIVED;
        return this.repository.update(summaryModel);
    }
    archiveWithId(id) {
        return this.repository.update({ id: id, contentId: id, state: CONTENT_STATE.ARCHIVED });
    }
    unArchive(summaryModel) {
        summaryModel.state = CONTENT_STATE.ACTIVE;
        return this.repository.update(summaryModel);
    }
    /**
     * DANGER: This will trigger a recursive delete of the entire content item and all of it's documents, files, history, etc.
     * Do not use this unless the intent to completely remove the entire content item, archive() might be a better choice?
     *
     * @param summaryModel
     * @returns
     */
    markForDelete(contentId) {
        const model = { id: contentId, contentId: contentId, state: CONTENT_STATE.DELETING };
        return this.repository.update(model);
    }
    isActive(state) {
        return state === CONTENT_STATE.ACTIVE;
    }
    isArchived(state) {
        return state === CONTENT_STATE.ARCHIVED;
    }
    isDeleting(state) {
        return state === CONTENT_STATE.DELETING;
    }
    isLocked(state) {
        return state === CONTENT_STATE.LOCKED;
    }
    isAdminUser(user) {
        if (user.roles) {
            return user.roles.includes(ADMINISTRATOR);
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSummaryService, deps: [{ token: ContentSummaryRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSummaryService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSummaryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentSummaryRepository }] });

class ContentPublishLogRepository extends BaseFirestoreRepository {
    buildPath(contentId, id = '') {
        return `${COLLECTIONS.COLLECTION_CONTENT}${contentId}${COLLECTIONS.COLLECTION_CONTENT_PUBLISH_LOG}${id}`;
    }
    get(contentId, logId) {
        const path = this.buildPath(contentId, logId);
        return super._get(path);
    }
    getValueChanges(contentId, logId) {
        const path = this.buildPath(contentId, logId);
        return super._getValueChanges(path);
    }
    getAll(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAll(path, queryBuilder.build());
    }
    getAllValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        return super._getAllValueChanges(path, queryBuilder.build());
    }
    getAllPublishingValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder();
        queryBuilder.where('customerKey', '==', customerKey);
        queryBuilder.where('state', '==', ContentPublishState.Publishing);
        return super._queryValueChanges(path, queryBuilder.build());
    }
    isPublishingValueChanges(contentId, customerKey) {
        const path = this.buildPath(contentId);
        const queryBuilder = new FirestoreQueryBuilder()
            .where('customerKey', '==', customerKey)
            .where('state', '==', ContentPublishState.Publishing)
            .limit(1);
        const publishingLog = super._queryValueChanges(path, queryBuilder.build());
        return publishingLog.pipe(map$1((publishingLog) => publishingLog.length > 0));
    }
    async add(model) {
        const path = this.buildPath(model.contentId);
        if (!model.id) {
            model.id = super.createId();
        }
        const output = await super._add(model, path);
        return output;
    }
    set() {
        throw new Error('Unsupported Operation');
    }
    update(model) {
        const path = this.buildPath(model.contentId, model.id);
        return super._update(model, path);
    }
    delete(contentId, logId) {
        throw new Error('Unsupported Operation');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentPublishLogRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentPublishLogRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentPublishLogRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class ContentPublishService {
    constructor(repository) {
        this.repository = repository;
    }
    get(contentId, logId) {
        return this.repository.get(contentId, logId);
    }
    getValueChanges(contentId, logId) {
        return this.repository.getValueChanges(contentId, logId);
    }
    getAll(contentId, customerKey) {
        return this.repository.getAll(contentId, customerKey);
    }
    getAllValueChanges(contentId, customerKey) {
        return this.repository.getAllValueChanges(contentId, customerKey);
    }
    getAllPublishingLogsValueChanges(contentId, customerKey) {
        return this.repository.getAllPublishingValueChanges(contentId, customerKey);
    }
    /**
     * ! Requires a getAll or an index
     * @deprecated use summary.lastPublishLog from content item
     */
    getLatestPublishedDate(contentId, customerKey) {
        return this.repository.getAll(contentId, customerKey).pipe(map$1((logs) => {
            if (!logs?.length)
                return undefined;
            const sortedArray = logs.sort((a, b) => {
                if (b.timestamp && a.timestamp) {
                    return b.timestamp?.getTime() - a.timestamp?.getTime();
                }
                else {
                    return 0;
                }
            });
            return sortedArray[0].timestamp;
        }));
    }
    isPublishing(contentId, customerKey) {
        return this.repository.isPublishingValueChanges(contentId, customerKey);
    }
    async add(publishRequest) {
        const document = publishRequest.document;
        const publishLog = {
            contentId: document.contentId,
            customerKey: document.customerKey,
            firstName: publishRequest.firstName,
            lastName: publishRequest.lastName,
            userId: publishRequest.userId,
            state: ContentPublishState.Publishing,
            id: this.repository.createId(),
            timestamp: new Date(),
        };
        return this.repository.add(publishLog);
    }
    update(model) {
        return this.repository.update(model);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentPublishService, deps: [{ token: ContentPublishLogRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentPublishService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentPublishService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentPublishLogRepository }] });

class ContentItemService {
    constructor(documentRepository, snapshotRepository, files, reviews, approvals, revisions, summary, publish) {
        this.documentRepository = documentRepository;
        this.snapshotRepository = snapshotRepository;
        this.files = files;
        this.reviews = reviews;
        this.approvals = approvals;
        this.revisions = revisions;
        this.summary = summary;
        this.publish = publish;
    }
    createId() {
        return this.documentRepository.createId();
    }
    get(contentId, languageCode, snapshotId) {
        const observables = new Array();
        if (snapshotId) {
            observables.push(this.snapshotRepository.get(contentId, snapshotId));
            observables.push(this.summary.get(contentId));
            return combineLatest(observables).pipe(map(([ci$, s$]) => this.mapItem(ci$.document, s$, snapshotId)));
        }
        else {
            observables.push(this.documentRepository.get(contentId, languageCode));
            observables.push(this.summary.get(contentId));
            return combineLatest(observables).pipe(map(([d$, s$]) => this.mapItem(d$, s$)));
        }
    }
    /**
     * ! Resource intensive call - only use for low volumes
     * In current architecture, must get all summaries and then fetch individual documents
     */
    getAllOfTypeForUser(user, contentType, languageCode) {
        return this.summary.getAllForUser(user, contentType).pipe(mergeMap((summaries) => this.summariesToDocuments(summaries, languageCode)));
    }
    /**
     * ! This calls for all summaries regardless of user permissions
     * ! Resource intensive call - only use for low volumes
     * In current architecture, must get all summaries and then fetch individual documents
     */
    getAllOfType(customerKey, contentType, languageCode) {
        return this.summary.getAll(customerKey, contentType).pipe(mergeMap((summaries) => this.summariesToDocuments(summaries, languageCode)));
    }
    summariesToDocuments(contentSummaries, languageCode) {
        if (!contentSummaries.length) {
            return of([]);
        }
        const contentItems$ = contentSummaries.map((summary) => this.summaryToDocument(summary, languageCode));
        return combineLatest(contentItems$);
    }
    summaryToDocument(summary, languageCode) {
        return this.documentRepository.get(summary.contentId, languageCode).pipe(map((document) => {
            return this.mapItem(document, summary); // Rest of interface assumes document is not undefined, though it may in fact be undefined
        }));
    }
    getValueChanges(contentId, languageCode, snapshotId) {
        const observables = new Array();
        if (snapshotId) {
            observables.push(this.snapshotRepository.getValueChanges(contentId, snapshotId));
            observables.push(this.summary.getValueChanges(contentId));
            return combineLatest(observables).pipe(map(([ci$, s$]) => this.mapItem(ci$.document, s$, snapshotId)));
        }
        else {
            observables.push(this.documentRepository.getValueChanges(contentId, languageCode));
            observables.push(this.summary.getValueChanges(contentId));
            return combineLatest(observables).pipe(map(([d$, s$]) => this.mapItem(d$, s$)));
        }
    }
    mapItem(document, summary, snapshotId) {
        const unpublishedChanges = document.__updatedAt > summary.lastPublishLog?.documentTimestamp; // if lastPublishLog is undefined it will forever be a draft
        return {
            customerKey: summary.customerKey,
            contentId: summary.contentId,
            state: summary.state,
            snapshotId: snapshotId,
            document: document,
            associations: summary.associations,
            firstPublishLog: summary.firstPublishLog,
            lastPublishLog: summary.lastPublishLog,
            unpublishedChanges,
        };
    }
    async persist(item, revision) {
        if (item.snapshotId) {
            await this.snapshotRepository.update({ contentId: item.contentId, document: item.document }, item.snapshotId);
            if (item.associations) {
                await this.summary.updateAssociations(item.contentId, item.associations);
            }
        }
        else {
            if (item.contentId) {
                await this.update(item, revision);
            }
            else {
                return await this.add(item, revision);
            }
        }
        return item;
    }
    async add(item, revision) {
        const documentItem = deepCopy(item);
        if (!documentItem.document) {
            throw new Error('Unable to add a content item with no document');
        }
        documentItem.contentId = this.createId();
        documentItem.document.contentId = documentItem.contentId;
        let revisionId = undefined;
        if (revision) {
            revision.contentId = documentItem.document.contentId;
            revisionId = await this.revisions.add(revision);
        }
        documentItem.document.revisionId = revisionId;
        const summaryModel = {
            id: documentItem.document.contentId,
            contentId: documentItem.document.contentId,
            contentType: documentItem.document.contentType,
            customerKey: documentItem.document.customerKey,
            title: documentItem.document.title,
            state: CONTENT_STATE.ACTIVE,
            description: documentItem.document.description,
            associations: documentItem.associations,
        };
        await this.summary.add(summaryModel);
        const document = (await this.documentRepository.add(documentItem.document));
        return { ...documentItem, document };
    }
    async update(item, revision) {
        const documentItem = deepCopy(item);
        if (!documentItem.document || !documentItem.contentId) {
            throw new Error('Unable to update, contentId or document is missing');
        }
        let revisionId = null;
        if (revision) {
            revisionId = await this.revisions.add(revision);
        }
        documentItem.document.revisionId = revisionId;
        documentItem.document.contentId = documentItem.contentId;
        documentItem.document.customerKey = documentItem.customerKey;
        await this.documentRepository.update(documentItem.document);
        if (documentItem.associations) {
            await this.summary.updateAssociations(documentItem.contentId, documentItem.associations);
        }
    }
    async updateDocument(document) {
        if (!document.contentId) {
            throw new Error('Unable to update - contentId is missing');
        }
        return this.documentRepository.update(document);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentItemService, deps: [{ token: ContentItemRepository }, { token: ContentSnapshotRepository }, { token: ContentFileService }, { token: ContentReviewService }, { token: ContentApprovalService }, { token: ContentRevisionService }, { token: ContentSummaryService }, { token: ContentPublishService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentItemService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentItemService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: ContentItemRepository }, { type: ContentSnapshotRepository }, { type: ContentFileService }, { type: ContentReviewService }, { type: ContentApprovalService }, { type: ContentRevisionService }, { type: ContentSummaryService }, { type: ContentPublishService }] });

class ContentSnapshotService {
    constructor(repository) {
        this.repository = repository;
    }
    get(contentId, snapshotId) {
        return this.repository.get(contentId, snapshotId);
    }
    getValueChanges(contentId, snapshotId) {
        return this.repository.getValueChanges(contentId, snapshotId);
    }
    getAll(contentId, customerKey) {
        return this.repository.getAll(contentId, customerKey);
    }
    getAllValueChanges(contentId, customerKey) {
        return this.repository.getAllValueChanges(contentId, customerKey);
    }
    add(snapshot) {
        return this.repository.add(snapshot);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSnapshotService, deps: [{ token: ContentSnapshotRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSnapshotService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentSnapshotService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentSnapshotRepository }] });

class EditContentAssociationsComponent {
    constructor(popoverGlobalService) {
        this.popoverGlobalService = popoverGlobalService;
        this.context = 'content-association';
        this.contentAssociations = new Map();
        this.users = new Array();
        this.side = 'left';
        this.readOnly = false;
        this.showDisplayName = true;
        this.onAssociationChange = new EventEmitter();
        this.selectedAssociations = new Map();
        this.selectedUsers = new Array();
        this.placeHolderText = 'Select User';
        this.showUsersDropdown = false;
        this.autocompleteOpened = false;
        this.popoverId = htmlId('content-association-popover');
    }
    ngOnInit() {
        this.selectedAssociations = deepCopy(this.contentAssociations) || new Map();
        this.setAvailableUsers();
    }
    ngOnChanges(changes) {
        if (changes.users?.previousValue !== changes.users?.currentValue) {
            this.setAvailableUsers();
        }
    }
    setPlaceHolderText() {
        if (!this.availableUsers || this.availableUsers.length < 1) {
            this.placeHolderText = 'No Users Available';
        }
        else {
            this.placeHolderText = 'Select Users';
        }
    }
    setAvailableUsers() {
        this.availableUsers = new Array();
        this.selectedUsers = new Array();
        if (!this.users) {
            return;
        }
        this.users.forEach((u) => {
            if (!this.selectedAssociations.has(u.id)) {
                this.availableUsers?.push({
                    displayName: this.showDisplayName && u.displayName ? u.displayName : `${u.firstName} ${u.lastName}`,
                    userId: u.id,
                    firstName: u.firstName,
                    lastName: u.lastName,
                    email: u.email,
                    photoURL: u.photoURL,
                });
            }
            else {
                this.selectedUsers.push({
                    displayName: this.showDisplayName && u.displayName ? u.displayName : `${u.firstName} ${u.lastName}`,
                    userId: u.id,
                    firstName: u.firstName,
                    lastName: u.lastName,
                    email: u.email,
                    photoURL: u.photoURL,
                });
            }
        });
        this.setPlaceHolderText();
    }
    onRemoveSelectedUser(selected) {
        let idx = this.selectedUsers.indexOf(selected);
        if (idx > -1) {
            this.selectedUsers.splice(idx, 1);
            this.availableUsers?.push(selected);
            this.selectedAssociations.delete(selected.userId);
            this.onAssociationChange.emit(this.selectedAssociations);
        }
        this.setPlaceHolderText();
    }
    onSelectUser(selected) {
        this.autocompleteOpened = false;
        this.selectedUsers.push(selected);
        const association = {
            userId: selected.userId,
            firstName: selected.firstName,
            lastName: selected.lastName,
            associationTypes: this.defaultAssociations || [],
        };
        this.selectedAssociations.set(selected.userId, association);
        this.setAvailableUsers();
        this.onAssociationChange.emit(this.selectedAssociations);
        this.setPlaceHolderText();
    }
    togglePopover() {
        this.showUsersDropdown = !this.showUsersDropdown;
        if (!this.showUsersDropdown) {
            this.popoverGlobalService.closePopover();
            return;
        }
        this.popoverGlobalService.openPopover({
            id: 'content-association-popover',
            content: this.associationContent,
            targetElement: this.popoverId,
            position: this.side,
            closeOnClickOutside: true,
            closeOnEscape: true,
            hasPadding: true,
            hasCloser: false,
            maxWidth: '320px',
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditContentAssociationsComponent, deps: [{ token: i1$1.E11PopoverGlobalService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: EditContentAssociationsComponent, selector: "edit-content-associations", inputs: { context: "context", contentAssociations: "contentAssociations", users: "users", currentUser: "currentUser", side: "side", defaultAssociations: "defaultAssociations", readOnly: "readOnly", showDisplayName: "showDisplayName" }, outputs: { onAssociationChange: "onAssociationChange" }, viewQueries: [{ propertyName: "associationContent", first: true, predicate: ["associationContent"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"context === 'team-members'; else contentAssociation\">\n  <ng-container *ngIf=\"users\">\n    <div class=\"e11-relative\">\n      <div class=\"e11-flex e11-w-fit\">\n        <div class=\"e11-flex e11-self-center e11-items-center\">\n          <p\n            [id]=\"popoverId\"\n            (click)=\"togglePopover()\"\n            *ngIf=\"!selectedUsers.length\"\n            class=\"e11-m-0 e11-w-full e11-flex e11-cursor-pointer e11-items-center e11-mb-1 e11-font-primary-demibold\"\n          >\n            {{ \"No Team Member Selected\" | translate }}\n          </p>\n          <div *ngIf=\"selectedUsers.length\" class=\"e11-flex\">\n            <ng-container *ngFor=\"let user of selectedUsers; let i = index; let isLast = last\">\n              <div *ngIf=\"i < 5\" class=\"e11--ml-[8px] hover:e11-z-[1]\">\n                <e11-tooltip\n                  [color]=\"'dark'\"\n                  [hideTooltip]=\"showUsersDropdown || readOnly\"\n                  [interactive]=\"true\"\n                  [position]=\"'bottom'\"\n                  [tooltipContent]=\"userTooltipContent\"\n                >\n                  <e11-avatar\n                    [id]=\"popoverId\"\n                    (click)=\"togglePopover()\"\n                    *ngIf=\"user\"\n                    [hasBorder]=\"true\"\n                    [imageUrl]=\"user.photoURL\"\n                    [initials]=\"user.firstName && user.lastName ? user.firstName.charAt(0) + user.lastName.charAt(0) : ''\"\n                    [size]=\"'md'\"\n                    class=\"e11-cursor-pointer\"\n                  ></e11-avatar>\n                </e11-tooltip>\n                <ng-template #userTooltipContent>\n                  <p class=\"e11-m-0\">{{ user?.displayName }}</p>\n                  <p class=\"e11-text-skin-light\">{{ user?.email }}</p>\n\n                  <div (click)=\"!(selectedUsers.length === 1) && onRemoveSelectedUser(user)\" *ngIf=\"!readOnly\" class=\"e11-w-1/12\">\n                    <div class=\"e11-flex\">\n                      <e11-tooltip\n                        [color]=\"'light'\"\n                        [hideTooltip]=\"selectedUsers.length > 1\"\n                        [tooltipText]=\"'You cannot remove the last hiring team member.' | translate\"\n                      >\n                        <button\n                          (click)=\"onRemoveSelectedUser(user)\"\n                          [ngClass]=\"{\n                            'e11-cursor-not-allowed e11-opacity-[0.5]': selectedUsers.length === 1,\n                            'hover:e11-text-skin-white': selectedUsers.length != 1\n                          }\"\n                          class=\"e11-text-skin-light hover:e11-text-skin-white\"\n                        >\n                          Remove\n                        </button>\n                      </e11-tooltip>\n                    </div>\n                  </div>\n                </ng-template>\n              </div>\n\n              <div\n                (click)=\"togglePopover()\"\n                *ngIf=\"selectedUsers.length > 5 && isLast\"\n                class=\"e11--ml-[8px] e11-z-0 e11-w-[42px] e11-h-[42px] e11-cursor-pointer e11-bg-skin-grey e11-flex e11-items-center e11-justify-center e11-rounded-full e11-border e11-border-skin-back e11-p-[2px]\"\n              >\n                + {{ selectedUsers.length - 5 }}\n              </div>\n            </ng-container>\n          </div>\n          <span [id]=\"popoverId\" (click)=\"togglePopover()\" class=\"e11-cursor-pointer material-icons-outlined\">expand_more</span>\n        </div>\n      </div>\n    </div>\n  </ng-container>\n</ng-container>\n\n<ng-template #contentAssociation>\n  <ng-container [ngTemplateOutlet]=\"associationContent\"></ng-container>\n</ng-template>\n\n<ng-template #associationContent>\n  <div [ngClass]=\"{ 'e11-min-h-[320px]': autocompleteOpened }\" *ngIf=\"availableUsers && !readOnly\">\n    <e11-autocomplete\n      (closed)=\"autocompleteOpened = false\"\n      (opened)=\"autocompleteOpened = true\"\n      (selected)=\"onSelectUser($event)\"\n      [data]=\"availableUsers\"\n      [disabled]=\"readOnly\"\n      [isLoading]=\"false\"\n      [isMultiSelect]=\"true\"\n      [itemTemplate]=\"itemTemplate\"\n      [notFoundTemplate]=\"notFoundTemplate\"\n      [placeholder]=\"placeHolderText\"\n      [searchKeyword]=\"'displayName'\"\n    >\n    </e11-autocomplete>\n    <ng-template #itemTemplate let-item>\n      <a [innerHTML]=\"item.displayName\"></a>\n    </ng-template>\n\n    <ng-template #notFoundTemplate let-notFound>\n      <div [innerHTML]=\"notFound\"></div>\n    </ng-template>\n  </div>\n\n  <div\n    *ngIf=\"defaultAssociations\"\n    [ngClass]=\"{ 'e11-overflow-hidden e11-hidden': autocompleteOpened }\"\n    class=\"e11-flex e11-gap-3 e11-flex-col e11-mt-4 e11-mb-4\"\n  >\n    <div *ngFor=\"let user of selectedUsers\" class=\"e11-flex e11-justify-between\">\n      <div class=\"e11-w-11/12\">\n        <p class=\"e11-truncate e11-font-primary-medium e11-w-full e11-m-0\">{{ user.displayName }}</p>\n        <p class=\"e11-text-sm e11-truncate e11-m-0\">({{ user.email }})</p>\n      </div>\n\n      <div (click)=\"!(selectedUsers.length === 1) && onRemoveSelectedUser(user)\" *ngIf=\"!readOnly\" class=\"e11-w-1/12\">\n        <div class=\"e11-flex\">\n          <e11-tooltip\n            [color]=\"'dark'\"\n            [tooltipText]=\"selectedUsers.length === 1 ? 'You cannot remove the last hiring team member.' : ('Remove from team' | translate)\"\n          >\n            <span\n              [ngClass]=\"{\n                'e11-cursor-not-allowed e11-opacity-[0.5]': selectedUsers.length === 1,\n                'hover:e11-text-skin-danger': selectedUsers.length != 1\n              }\"\n              class=\"material-icons-outlined material-icons-round e11-cursor-pointer\"\n            >\n              delete_outline\n            </span>\n          </e11-tooltip>\n        </div>\n      </div>\n    </div>\n  </div>\n</ng-template>\n", styles: [".selected-user{margin:1rem;width:100%;max-width:400px;border-bottom:1px solid lightgray;padding-bottom:1rem}.selected-user .display-name{text-overflow:ellipsis;white-space:nowrap;overflow:hidden;font-weight:700}.selected-user .email{font-size:.8rem}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: i3.E11AutocompleteComponent, selector: "e11-autocomplete", inputs: ["name", "label", "placeholder", "heading", "required", "initialValue", "data", "selectedData", "searchKeyword", "isMultiSelect", "notFoundText", "isLoading", "skeletonLoading", "debounceTime", "disabled", "minQueryLength", "focusFirst", "stopSelectEvent", "customFilter", "formControlName", "parentForm", "hasError", "itemTemplate", "notFoundTemplate"], outputs: ["selected", "inputChanged", "inputFocused", "inputCleared", "opened", "closed", "scrolledToEnd"] }, { kind: "component", type: i4.E11TooltipComponent, selector: "e11-tooltip", inputs: ["tooltipText", "tooltipClassOverrides", "hideTooltip", "color", "tooltipContent", "position", "interactive", "maxWidth", "delay"], outputs: ["tooltipClick"] }, { kind: "component", type: i5.E11AvatarComponent, selector: "e11-avatar", inputs: ["colorSeed", "initials", "size", "shape", "sizeOverride", "imageUrl", "imageTitleText", "imageFit", "editable", "regenerate", "hasBorder", "hasRing"], outputs: ["edit", "onEdit"] }, { kind: "pipe", type: i6.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditContentAssociationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'edit-content-associations', template: "<ng-container *ngIf=\"context === 'team-members'; else contentAssociation\">\n  <ng-container *ngIf=\"users\">\n    <div class=\"e11-relative\">\n      <div class=\"e11-flex e11-w-fit\">\n        <div class=\"e11-flex e11-self-center e11-items-center\">\n          <p\n            [id]=\"popoverId\"\n            (click)=\"togglePopover()\"\n            *ngIf=\"!selectedUsers.length\"\n            class=\"e11-m-0 e11-w-full e11-flex e11-cursor-pointer e11-items-center e11-mb-1 e11-font-primary-demibold\"\n          >\n            {{ \"No Team Member Selected\" | translate }}\n          </p>\n          <div *ngIf=\"selectedUsers.length\" class=\"e11-flex\">\n            <ng-container *ngFor=\"let user of selectedUsers; let i = index; let isLast = last\">\n              <div *ngIf=\"i < 5\" class=\"e11--ml-[8px] hover:e11-z-[1]\">\n                <e11-tooltip\n                  [color]=\"'dark'\"\n                  [hideTooltip]=\"showUsersDropdown || readOnly\"\n                  [interactive]=\"true\"\n                  [position]=\"'bottom'\"\n                  [tooltipContent]=\"userTooltipContent\"\n                >\n                  <e11-avatar\n                    [id]=\"popoverId\"\n                    (click)=\"togglePopover()\"\n                    *ngIf=\"user\"\n                    [hasBorder]=\"true\"\n                    [imageUrl]=\"user.photoURL\"\n                    [initials]=\"user.firstName && user.lastName ? user.firstName.charAt(0) + user.lastName.charAt(0) : ''\"\n                    [size]=\"'md'\"\n                    class=\"e11-cursor-pointer\"\n                  ></e11-avatar>\n                </e11-tooltip>\n                <ng-template #userTooltipContent>\n                  <p class=\"e11-m-0\">{{ user?.displayName }}</p>\n                  <p class=\"e11-text-skin-light\">{{ user?.email }}</p>\n\n                  <div (click)=\"!(selectedUsers.length === 1) && onRemoveSelectedUser(user)\" *ngIf=\"!readOnly\" class=\"e11-w-1/12\">\n                    <div class=\"e11-flex\">\n                      <e11-tooltip\n                        [color]=\"'light'\"\n                        [hideTooltip]=\"selectedUsers.length > 1\"\n                        [tooltipText]=\"'You cannot remove the last hiring team member.' | translate\"\n                      >\n                        <button\n                          (click)=\"onRemoveSelectedUser(user)\"\n                          [ngClass]=\"{\n                            'e11-cursor-not-allowed e11-opacity-[0.5]': selectedUsers.length === 1,\n                            'hover:e11-text-skin-white': selectedUsers.length != 1\n                          }\"\n                          class=\"e11-text-skin-light hover:e11-text-skin-white\"\n                        >\n                          Remove\n                        </button>\n                      </e11-tooltip>\n                    </div>\n                  </div>\n                </ng-template>\n              </div>\n\n              <div\n                (click)=\"togglePopover()\"\n                *ngIf=\"selectedUsers.length > 5 && isLast\"\n                class=\"e11--ml-[8px] e11-z-0 e11-w-[42px] e11-h-[42px] e11-cursor-pointer e11-bg-skin-grey e11-flex e11-items-center e11-justify-center e11-rounded-full e11-border e11-border-skin-back e11-p-[2px]\"\n              >\n                + {{ selectedUsers.length - 5 }}\n              </div>\n            </ng-container>\n          </div>\n          <span [id]=\"popoverId\" (click)=\"togglePopover()\" class=\"e11-cursor-pointer material-icons-outlined\">expand_more</span>\n        </div>\n      </div>\n    </div>\n  </ng-container>\n</ng-container>\n\n<ng-template #contentAssociation>\n  <ng-container [ngTemplateOutlet]=\"associationContent\"></ng-container>\n</ng-template>\n\n<ng-template #associationContent>\n  <div [ngClass]=\"{ 'e11-min-h-[320px]': autocompleteOpened }\" *ngIf=\"availableUsers && !readOnly\">\n    <e11-autocomplete\n      (closed)=\"autocompleteOpened = false\"\n      (opened)=\"autocompleteOpened = true\"\n      (selected)=\"onSelectUser($event)\"\n      [data]=\"availableUsers\"\n      [disabled]=\"readOnly\"\n      [isLoading]=\"false\"\n      [isMultiSelect]=\"true\"\n      [itemTemplate]=\"itemTemplate\"\n      [notFoundTemplate]=\"notFoundTemplate\"\n      [placeholder]=\"placeHolderText\"\n      [searchKeyword]=\"'displayName'\"\n    >\n    </e11-autocomplete>\n    <ng-template #itemTemplate let-item>\n      <a [innerHTML]=\"item.displayName\"></a>\n    </ng-template>\n\n    <ng-template #notFoundTemplate let-notFound>\n      <div [innerHTML]=\"notFound\"></div>\n    </ng-template>\n  </div>\n\n  <div\n    *ngIf=\"defaultAssociations\"\n    [ngClass]=\"{ 'e11-overflow-hidden e11-hidden': autocompleteOpened }\"\n    class=\"e11-flex e11-gap-3 e11-flex-col e11-mt-4 e11-mb-4\"\n  >\n    <div *ngFor=\"let user of selectedUsers\" class=\"e11-flex e11-justify-between\">\n      <div class=\"e11-w-11/12\">\n        <p class=\"e11-truncate e11-font-primary-medium e11-w-full e11-m-0\">{{ user.displayName }}</p>\n        <p class=\"e11-text-sm e11-truncate e11-m-0\">({{ user.email }})</p>\n      </div>\n\n      <div (click)=\"!(selectedUsers.length === 1) && onRemoveSelectedUser(user)\" *ngIf=\"!readOnly\" class=\"e11-w-1/12\">\n        <div class=\"e11-flex\">\n          <e11-tooltip\n            [color]=\"'dark'\"\n            [tooltipText]=\"selectedUsers.length === 1 ? 'You cannot remove the last hiring team member.' : ('Remove from team' | translate)\"\n          >\n            <span\n              [ngClass]=\"{\n                'e11-cursor-not-allowed e11-opacity-[0.5]': selectedUsers.length === 1,\n                'hover:e11-text-skin-danger': selectedUsers.length != 1\n              }\"\n              class=\"material-icons-outlined material-icons-round e11-cursor-pointer\"\n            >\n              delete_outline\n            </span>\n          </e11-tooltip>\n        </div>\n      </div>\n    </div>\n  </div>\n</ng-template>\n", styles: [".selected-user{margin:1rem;width:100%;max-width:400px;border-bottom:1px solid lightgray;padding-bottom:1rem}.selected-user .display-name{text-overflow:ellipsis;white-space:nowrap;overflow:hidden;font-weight:700}.selected-user .email{font-size:.8rem}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.E11PopoverGlobalService }], propDecorators: { associationContent: [{
                type: ViewChild,
                args: ['associationContent']
            }], context: [{
                type: Input
            }], contentAssociations: [{
                type: Input
            }], users: [{
                type: Input
            }], currentUser: [{
                type: Input
            }], side: [{
                type: Input
            }], defaultAssociations: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], showDisplayName: [{
                type: Input
            }], onAssociationChange: [{
                type: Output
            }] } });

const initialState = {
    selectedContent: undefined,
    contentLoading: false,
};
class ContentStore extends ComponentStore {
    constructor(contentSummaryService, errorHandler) {
        super(initialState);
        this.contentSummaryService = contentSummaryService;
        this.errorHandler = errorHandler;
        // SELECTORS
        this.getState = this.select((s) => s);
        this.getSelectedContent = () => this.select((s) => s.selectedContent);
        this.loadingContent = () => this.select((s) => s.contentLoading);
        // EFFECTS
        this.onGetContent = this.effect((contentId$) => {
            return contentId$.pipe(switchMap((contentId) => {
                this.setContentLoading();
                return this.contentSummaryService.get(contentId).pipe(tapResponse((content) => this.onContentFetched(content), (e) => this.errorHandler.handleError(e)));
            }));
        });
        // UPDATERS
        this.setContentLoading = this.updater((state) => ({
            ...state,
            contentLoading: true,
            selectedContent: undefined,
        }));
        this.onContentFetched = this.updater((state, content) => ({
            ...state,
            selectedContent: content,
            contentLoading: false,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentStore, deps: [{ token: ContentSummaryService }, { token: i2$1.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: ContentSummaryService }, { type: i2$1.E11ErrorHandlerService }] });

class ViewContentAssociationsComponent {
    set contentId(contentId) {
        if (!contentId)
            return;
        this.store.onGetContent(contentId);
    }
    constructor(store) {
        this.store = store;
        this.users = new Array();
        this.showDisplayName = true;
        this.side = 'bottom-right';
        this.selectedContent$ = this.store.getSelectedContent();
        this.loadingContent$ = this.store.loadingContent();
    }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ViewContentAssociationsComponent, deps: [{ token: ContentStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: ViewContentAssociationsComponent, selector: "sdk-content-view-associations", inputs: { users: "users", defaultAssociations: "defaultAssociations", showDisplayName: "showDisplayName", side: "side", contentId: "contentId" }, ngImport: i0, template: "<ng-container *ngrxLet=\"selectedContent$ as content\">\n  <ng-container *ngrxLet=\"loadingContent$ as loadingContent\">\n    <edit-content-associations\n      *ngIf=\"!loadingContent && content\"\n      [contentAssociations]=\"content.associations!\"\n      [context]=\"'team-members'\"\n      [defaultAssociations]=\"defaultAssociations\"\n      [readOnly]=\"true\"\n      [users]=\"users\"\n      [side]=\"side\"\n      [showDisplayName]=\"showDisplayName\"\n    >\n    </edit-content-associations>\n\n    <div *ngIf=\"loadingContent\">\n      <div *ngFor=\"let i of 4 | range\" class=\"e11-inline-block e11--mr-2\">\n        <e11-profile-pic [hasBorder]=\"true\" [loadingImageUrl]=\"true\" [size]=\"'md'\" [type]=\"'circular'\"></e11-profile-pic>\n      </div>\n    </div>\n  </ng-container>\n</ng-container>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: i4$1.E11ProfilePicComponent, selector: "e11-profile-pic", inputs: ["value", "style", "imageUrl", "imageFit", "size", "edit", "disabled", "type", "hasBorder", "hasRing", "icon", "iconPositionY", "iconPositionX", "textValue", "imageClass", "textClass", "iconClass", "loadingImageUrl"], outputs: ["editProfilePic"] }, { kind: "component", type: EditContentAssociationsComponent, selector: "edit-content-associations", inputs: ["context", "contentAssociations", "users", "currentUser", "side", "defaultAssociations", "readOnly", "showDisplayName"], outputs: ["onAssociationChange"] }, { kind: "pipe", type: i6$1.RangePipe, name: "range" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ViewContentAssociationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-content-view-associations', template: "<ng-container *ngrxLet=\"selectedContent$ as content\">\n  <ng-container *ngrxLet=\"loadingContent$ as loadingContent\">\n    <edit-content-associations\n      *ngIf=\"!loadingContent && content\"\n      [contentAssociations]=\"content.associations!\"\n      [context]=\"'team-members'\"\n      [defaultAssociations]=\"defaultAssociations\"\n      [readOnly]=\"true\"\n      [users]=\"users\"\n      [side]=\"side\"\n      [showDisplayName]=\"showDisplayName\"\n    >\n    </edit-content-associations>\n\n    <div *ngIf=\"loadingContent\">\n      <div *ngFor=\"let i of 4 | range\" class=\"e11-inline-block e11--mr-2\">\n        <e11-profile-pic [hasBorder]=\"true\" [loadingImageUrl]=\"true\" [size]=\"'md'\" [type]=\"'circular'\"></e11-profile-pic>\n      </div>\n    </div>\n  </ng-container>\n</ng-container>\n" }]
        }], ctorParameters: () => [{ type: ContentStore }], propDecorators: { users: [{
                type: Input
            }], defaultAssociations: [{
                type: Input
            }], showDisplayName: [{
                type: Input
            }], side: [{
                type: Input
            }], contentId: [{
                type: Input
            }] } });

const declareExportComponents = [EditContentAssociationsComponent, ViewContentAssociationsComponent];
class ContentModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: ContentModule, declarations: [EditContentAssociationsComponent, ViewContentAssociationsComponent], imports: [CommonModule,
            E11AutocompleteModule,
            E11TooltipModule,
            TranslateModule,
            E11AvatarModule,
            WebUtilityModule,
            LetDirective,
            E11ProfilePicModule], exports: [EditContentAssociationsComponent, ViewContentAssociationsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentModule, imports: [CommonModule,
            E11AutocompleteModule,
            E11TooltipModule,
            TranslateModule,
            E11AvatarModule,
            WebUtilityModule,
            E11ProfilePicModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ContentModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...declareExportComponents],
                    imports: [
                        CommonModule,
                        E11AutocompleteModule,
                        E11TooltipModule,
                        TranslateModule,
                        E11AvatarModule,
                        WebUtilityModule,
                        LetDirective,
                        E11ProfilePicModule,
                    ],
                    exports: [...declareExportComponents],
                }]
        }] });

function documentIdToContentId(docId) {
    return docId.split('_')[0];
}

/*
 * Public API Surface of content
 */
// export * from './lib/content.service'
// export * from './lib/content.component'
// export * from './lib/content.module'

/**
 * Generated bundle index. Do not edit.
 */

export { COLLECTIONS, CONTENT_ASSOCIATION, CONTENT_STATE, ContentApprovalCommentRepository, ContentApprovalRepository, ContentApprovalRequestRepository, ContentApprovalRequestService, ContentApprovalService, ContentDocumentRepository, ContentFileService, ContentItemRepository, ContentItemService, ContentModule, ContentPublishLogRepository, ContentPublishService, ContentPublishState, ContentReviewCommentRepository, ContentReviewRepository, ContentReviewRequestRepository, ContentReviewRequestService, ContentReviewService, ContentRevisionRepository, ContentRevisionService, ContentSnapshotRepository, ContentSnapshotService, ContentSummaryRepository, ContentSummaryService, DEFAULT_LANGUAGE_CODE, ERROR_TYPES, EditContentAssociationsComponent, FILES, ViewContentAssociationsComponent, documentIdToContentId };
